import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Archive, BellRing, Building2, CalendarDays, Check, CheckCircle2, ChevronLeft,
  ChevronRight, ChevronDown, Circle, Clock3, ListChecks, Mic, MicOff, Pencil, Pin, Plus,
  Search, StickyNote, Trash2, UserRound,
} from "lucide-react";

const EMPTY = {
  title: "", content: "", category: "firma", pinned: false, kind: "note",
  checklist: [], reminder_at: null, completed: false,
};
const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "firma", label: "Firma" },
  { key: "privat", label: "Privat" },
];

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";

const dayKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const localInputValue = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const reminderState = (note) => {
  if (!note.reminder_at || note.completed) return "none";
  const when = new Date(note.reminder_at);
  const now = new Date();
  if (when < now) return "overdue";
  if (dayKey(when) === dayKey(now)) return "today";
  return "upcoming";
};

const KIND_LABELS = { note: "Notiz", task: "Aufgabe", checklist: "Checkliste" };
const SCOPES = [
  { key: "all", label: "Alle" },
  { key: "today", label: "Heute" },
  { key: "upcoming", label: "Demnächst" },
  { key: "overdue", label: "Überfällig" },
  { key: "done", label: "Erledigt" },
];

export default function Notes() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [scope, setScope] = useState("all");
  const [selectedDay, setSelectedDay] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [mobileCalendarOpen, setMobileCalendarOpen] = useState(false);
  const [speechLang, setSpeechLang] = useState("de-DE");
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const speechSupported = typeof window !== "undefined"
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const stopDictation = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  };

  useEffect(() => () => {
    if (recognitionRef.current) recognitionRef.current.abort();
  }, []);

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["notes"],
    queryFn: async () => (await api.get("/notes")).data,
  });

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return notes.filter((note) => {
      if (filter !== "all" && note.category !== filter) return false;
      if (selectedDay && dayKey(note.reminder_at) !== selectedDay) return false;
      const state = reminderState(note);
      if (scope === "done" && !note.completed) return false;
      if (scope !== "all" && scope !== "done" && state !== scope) return false;
      if (scope === "all" && note.completed) return false;
      if (!needle) return true;
      return `${note.title} ${note.content}`.toLowerCase().includes(needle);
    });
  }, [notes, filter, scope, selectedDay, query]);

  const reminderDays = useMemo(() => new Set(notes.filter((note) => note.reminder_at && !note.completed).map((note) => dayKey(note.reminder_at))), [notes]);

  const calendarCells = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const first = new Date(year, month, 1);
    const leading = (first.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - leading);
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return { date, key: dayKey(date), current: date.getMonth() === month };
    });
  }, [calendarMonth]);

  const saveMut = useMutation({
    mutationFn: async () => editing
      ? (await api.put(`/notes/${editing.id}`, form)).data
      : (await api.post("/notes", form)).data,
    onSuccess: () => {
      toast.success(editing ? "Notiz aktualisiert" : "Notiz gespeichert");
      qc.invalidateQueries({ queryKey: ["notes"] });
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
    },
    onError: (error) => toast.error(apiError(error)),
  });

  const pinMut = useMutation({
    mutationFn: async (id) => (await api.patch(`/notes/${id}/pin`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
    onError: (error) => toast.error(apiError(error)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/notes/${id}`)).data,
    onSuccess: () => {
      toast.success("Notiz gelöscht");
      qc.invalidateQueries({ queryKey: ["notes"] });
    },
    onError: (error) => toast.error(apiError(error)),
  });

  const completeMut = useMutation({
    mutationFn: async (id) => (await api.patch(`/notes/${id}/complete`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
    onError: (error) => toast.error(apiError(error)),
  });

  const newNote = () => {
    setEditing(null);
    setForm({ ...EMPTY, category: filter === "privat" ? "privat" : "firma" });
    setOpen(true);
  };

  const editNote = (note) => {
    stopDictation();
    setEditing(note);
    setForm({
      title: note.title || "",
      content: note.content || "",
      category: note.category || "firma",
      pinned: !!note.pinned,
      kind: note.kind || "note",
      checklist: note.checklist || [],
      reminder_at: note.reminder_at || null,
      completed: !!note.completed,
    });
    setOpen(true);
  };

  const startDictation = ({ createNew = false } = {}) => {
    if (!speechSupported) {
      toast.error("Spracherkennung wird von diesem Browser nicht unterstützt");
      return;
    }

    if (listening) {
      stopDictation();
      return;
    }

    if (createNew) {
      setEditing(null);
      setForm({ ...EMPTY, category: filter === "privat" ? "privat" : "firma" });
      setOpen(true);
    }

    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new Recognition();
    recognition.lang = speechLang;
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let transcript = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        if (event.results[index].isFinal) transcript += event.results[index][0].transcript;
      }
      const clean = transcript.trim();
      if (clean) {
        setForm((current) => ({
          ...current,
          content: `${current.content}${current.content.trim() ? " " : ""}${clean}`,
        }));
      }
    };
    recognition.onerror = (event) => {
      recognitionRef.current = null;
      setListening(false);
      if (event.error !== "aborted" && event.error !== "no-speech") {
        toast.error(event.error === "not-allowed" ? "Mikrofonzugriff wurde nicht erlaubt" : "Diktat konnte nicht gestartet werden");
      }
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch {
      recognitionRef.current = null;
      setListening(false);
      toast.error("Diktat konnte nicht gestartet werden");
    }
  };

  const handleDialogChange = (nextOpen) => {
    if (!nextOpen) stopDictation();
    setOpen(nextOpen);
  };

  const addChecklistItem = () => {
    const id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    setForm((current) => ({ ...current, checklist: [...current.checklist, { id, text: "", done: false }] }));
  };

  const updateChecklistItem = (id, patch) => {
    setForm((current) => ({
      ...current,
      checklist: current.checklist.map((item) => item.id === id ? { ...item, ...patch } : item),
    }));
  };

  return (
    <div className="stellar-page max-w-7xl">
      <div className="stellar-page-heading stellar-heading-indigo flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="stellar-module-icon stellar-module-indigo"><StickyNote /></div>
          <div>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">Notizen</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Geschäftliche und private Notizen an einem Ort.</p>
          </div>
        </div>
        <div className="w-full lg:w-auto lg:ml-auto flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Notizen durchsuchen…"
              className="pl-8 w-full sm:w-64 bg-white"
              data-testid="notes-search"
            />
          </div>
          <Button onClick={newNote} className="rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="new-note">
            <Plus className="h-4 w-4 mr-1" /> Neue Notiz
          </Button>
        </div>
      </div>

      <div className="stellar-segmented mb-5">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            className={`px-4 py-1.5 text-sm rounded-lg ${filter === item.key ? "bg-white text-indigo-700 shadow-sm font-semibold" : "text-muted-foreground"}`}
            data-testid={`notes-filter-${item.key}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="premium-card p-4">
          <div className="mb-3 flex items-center gap-2 font-display font-semibold">
            <Clock3 className="h-4 w-4 text-indigo-600" /> Übersicht
          </div>
          <div className="flex flex-wrap gap-2">
            {SCOPES.map((item) => {
              const count = notes.filter((note) => {
                if (item.key === "all") return !note.completed;
                if (item.key === "done") return note.completed;
                return reminderState(note) === item.key;
              }).length;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => { setScope(item.key); setSelectedDay(""); }}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${scope === item.key && !selectedDay ? "border-indigo-300 bg-indigo-50 text-indigo-800" : "border-border/60 bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {item.label} <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px]">{count}</span>
                </button>
              );
            })}
          </div>
          {selectedDay && (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
              <CalendarDays className="h-4 w-4" /> {new Date(`${selectedDay}T12:00:00`).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}
              <button type="button" onClick={() => setSelectedDay("")} className="ml-auto font-semibold hover:underline">Alle anzeigen</button>
            </div>
          )}
        </div>

        <div className="premium-card overflow-hidden p-0 lg:p-3">
          <button
            type="button"
            onClick={() => setMobileCalendarOpen((current) => !current)}
            className="flex w-full items-center gap-2 px-4 py-3 text-left lg:hidden"
            aria-expanded={mobileCalendarOpen}
          >
            <CalendarDays className="h-4 w-4 text-indigo-600" />
            <span className="font-display text-sm font-semibold">Kalender</span>
            <span className="ml-auto text-xs capitalize text-muted-foreground">{calendarMonth.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}</span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${mobileCalendarOpen ? "rotate-180" : ""}`} />
          </button>
          <div className={`${mobileCalendarOpen ? "block" : "hidden"} border-t px-3 pb-3 pt-2 lg:block lg:border-0 lg:p-0`}>
          <div className="mb-2 flex items-center justify-between">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="font-display text-sm font-semibold capitalize">{calendarMonth.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}</div>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-7 text-center text-[10px] font-semibold uppercase text-slate-400">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => <div key={day} className="py-1">{day}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {calendarCells.map((cell) => {
              const active = selectedDay === cell.key;
              const today = cell.key === dayKey(new Date());
              const marked = reminderDays.has(cell.key);
              return (
                <button
                  key={cell.key}
                  type="button"
                  onClick={() => { setSelectedDay(active ? "" : cell.key); setScope("all"); }}
                  className={`relative flex h-8 items-center justify-center rounded-lg text-xs ${!cell.current ? "text-slate-300" : "text-slate-700"} ${active ? "bg-indigo-600 font-bold text-white" : today ? "bg-emerald-100 font-bold text-emerald-800" : "hover:bg-slate-100"}`}
                >
                  {cell.date.getDate()}
                  {marked && <span className={`absolute bottom-0.5 h-1 w-1 rounded-full ${active ? "bg-white" : "bg-red-500"}`} />}
                </button>
              );
            })}
          </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Laden…</div>
      ) : visible.length === 0 ? (
        <div className="premium-card py-16 text-center text-sm text-muted-foreground">
          <StickyNote className="h-10 w-10 mx-auto mb-3 opacity-35" />
          <div className="font-medium text-foreground mb-1">Keine Notizen.</div>
          <div className="text-xs">Mit „Neue Notiz“ können Sie die erste Notiz anlegen.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((note) => {
            const personal = note.category === "privat";
            const kind = note.kind || "note";
            const state = reminderState(note);
            const doneItems = (note.checklist || []).filter((item) => item.done).length;
            const expanded = expandedNoteId === note.id;
            return (
              <article
                key={note.id}
                className={`premium-card relative overflow-hidden transition-shadow ${personal ? "border-violet-200/80" : "border-emerald-200/80"} ${note.completed ? "opacity-70" : ""} ${expanded ? "shadow-md" : ""}`}
                data-testid={`note-${note.id}`}
              >
                <div className={`absolute inset-x-0 top-0 h-1 ${personal ? "bg-violet-400" : "bg-emerald-500"}`} />
                <button
                  type="button"
                  onClick={() => setExpandedNoteId(expanded ? null : note.id)}
                  className="flex w-full items-center gap-3 px-4 py-3.5 text-left sm:px-5 sm:py-4"
                  aria-expanded={expanded}
                >
                  <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${personal ? "bg-violet-50 text-violet-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {kind === "task" ? <CheckCircle2 className="h-4 w-4" /> : kind === "checklist" ? <ListChecks className="h-4 w-4" /> : personal ? <UserRound className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="font-display font-semibold truncate">{note.title || "Ohne Titel"}</h2>
                      {note.pinned && <Pin className="h-3.5 w-3.5 text-amber-600 fill-amber-100 shrink-0" />}
                    </div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span>{personal ? "Privat" : "Firma"}</span><span>·</span><span>{KIND_LABELS[kind]}</span>
                      <span className="hidden sm:inline">·</span><span className="hidden sm:inline">{fmtDate(note.updated_at)}</span>
                    </div>
                    {!expanded && note.content && <p className="mt-1 truncate text-xs text-slate-500">{note.content}</p>}
                  </div>
                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    {kind === "checklist" && (note.checklist || []).length > 0 && (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-600">{doneItems}/{note.checklist.length}</span>
                    )}
                    {note.reminder_at && (
                      <span className={`hidden items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-semibold sm:inline-flex ${state === "overdue" ? "bg-red-50 text-red-700" : state === "today" ? "bg-amber-50 text-amber-700" : note.completed ? "bg-slate-100 text-slate-500" : "bg-blue-50 text-blue-700"}`}>
                        <BellRing className="h-3 w-3" /> {new Date(note.reminder_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                      </span>
                    )}
                    <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {expanded && <div className="border-t border-border/60 px-4 pb-4 sm:px-5 sm:pb-5">
                {note.reminder_at && (
                  <div className={`mt-3 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${state === "overdue" ? "bg-red-50 text-red-700" : state === "today" ? "bg-amber-50 text-amber-700" : note.completed ? "bg-slate-100 text-slate-500" : "bg-blue-50 text-blue-700"}`}>
                    <BellRing className="h-3.5 w-3.5" /> {fmtDate(note.reminder_at)}
                  </div>
                )}

                {note.content && (
                  <div className={`mt-4 max-h-52 overflow-hidden whitespace-pre-wrap text-sm leading-relaxed text-slate-700 ${note.completed ? "line-through" : ""}`}>
                    {note.content}
                  </div>
                )}

                {kind === "checklist" && (note.checklist || []).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {(note.checklist || []).slice(0, 5).map((item) => (
                      <div key={item.id} className={`flex items-start gap-2 text-sm ${item.done ? "text-slate-400 line-through" : "text-slate-700"}`}>
                        {item.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
                        <span>{item.text}</span>
                      </div>
                    ))}
                    <div className="text-[11px] font-semibold text-muted-foreground">{doneItems}/{note.checklist.length} erledigt</div>
                  </div>
                )}

                <div className="flex items-center gap-1 mt-4 pt-3 border-t border-border/60">
                  {kind !== "note" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => completeMut.mutate(note.id)}
                      className={note.completed ? "text-slate-600" : "text-emerald-700"}
                    >
                      {note.completed ? <Archive className="h-3.5 w-3.5 mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                      {note.completed ? "Öffnen" : "Erledigt"}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => pinMut.mutate(note.id)}
                    className={`${kind !== "note" ? "hidden sm:inline-flex" : ""} ${note.pinned ? "text-amber-700" : "text-muted-foreground"}`}
                    data-testid={`pin-note-${note.id}`}
                  >
                    <Pin className="h-3.5 w-3.5 mr-1" /> {note.pinned ? "Lösen" : "Fixieren"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => editNote(note)} className="ml-auto" data-testid={`edit-note-${note.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => { if (confirm("Notiz wirklich löschen?")) deleteMut.mutate(note.id); }}
                    data-testid={`delete-note-${note.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                </div>}
              </article>
            );
          })}
        </div>
      )}

      {speechSupported && (
        <Button
          type="button"
          size="icon"
          onClick={() => startDictation({ createNew: true })}
          className={`fixed bottom-5 right-4 z-40 h-14 w-14 rounded-full shadow-xl md:hidden ${listening ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"}`}
          aria-label={listening ? "Diktat stoppen" : "Neue Notiz diktieren"}
          title={listening ? "Diktat stoppen" : "Neue Notiz diktieren"}
          data-testid="quick-dictation"
        >
          {listening ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
        </Button>
      )}

      <Dialog open={open} onOpenChange={handleDialogChange}>
        <DialogContent className="flex max-h-[92dvh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-5 py-4 pr-12 sm:px-6">
            <DialogTitle>{editing ? "Notiz bearbeiten" : "Neue Notiz"}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 sm:px-6">
            <div>
              <Label>Titel (optional)</Label>
              <Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Kurzer Titel…" data-testid="note-title" />
            </div>
            <div>
              <Label>Typ</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {[
                  { key: "note", label: "Notiz", icon: StickyNote },
                  { key: "task", label: "Aufgabe", icon: CheckCircle2 },
                  { key: "checklist", label: "Checkliste", icon: ListChecks },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setForm({ ...form, kind: item.key, checklist: item.key === "checklist" ? form.checklist : [] })}
                      className={`rounded-xl border p-2.5 text-center text-xs sm:text-sm ${form.kind === item.key ? "border-indigo-400 bg-indigo-50 font-semibold text-indigo-800" : "border-border/60"}`}
                    >
                      <Icon className="mx-auto mb-1 h-4 w-4" />{item.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {form.kind === "checklist" && (
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-3 sm:p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Label className="font-semibold">Checklisten-Einträge</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addChecklistItem}><Plus className="mr-1 h-4 w-4" /> Eintrag</Button>
                </div>
                <div className="space-y-2">
                  {form.checklist.map((item) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <button type="button" onClick={() => updateChecklistItem(item.id, { done: !item.done })} className="shrink-0 text-emerald-600">
                        {item.done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5 text-slate-300" />}
                      </button>
                      <Input value={item.text} onChange={(event) => updateChecklistItem(item.id, { text: event.target.value })} placeholder="Eintrag…" />
                      <Button type="button" variant="ghost" size="icon" className="shrink-0 text-destructive" onClick={() => setForm((current) => ({ ...current, checklist: current.checklist.filter((entry) => entry.id !== item.id) }))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {form.checklist.length === 0 && <button type="button" onClick={addChecklistItem} className="w-full rounded-xl border border-dashed bg-white/70 p-4 text-sm text-muted-foreground">Ersten Eintrag hinzufügen</button>}
                </div>
              </div>
            )}
            <div>
              <Label>Kategorie</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, category: "firma" })}
                  className={`rounded-xl border p-3 text-left ${form.category === "firma" ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-border/60"}`}
                  data-testid="note-category-firma"
                >
                  <Building2 className="h-4 w-4 inline mr-2" />Firma
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, category: "privat" })}
                  className={`rounded-xl border p-3 text-left ${form.category === "privat" ? "border-violet-400 bg-violet-50 text-violet-800" : "border-border/60"}`}
                  data-testid="note-category-privat"
                >
                  <UserRound className="h-4 w-4 inline mr-2" />Privat
                </button>
              </div>
            </div>
            <div>
              <Label>Erinnerung (optional)</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  type="datetime-local"
                  value={localInputValue(form.reminder_at)}
                  onChange={(event) => setForm({ ...form, reminder_at: event.target.value ? new Date(event.target.value).toISOString() : null })}
                  className="flex-1"
                  data-testid="note-reminder"
                />
                {form.reminder_at && (
                  <Button type="button" variant="outline" onClick={() => setForm({ ...form, reminder_at: null })}>Löschen</Button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button type="button" onClick={() => { const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0); setForm({ ...form, reminder_at: d.toISOString() }); }} className="rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600">In 1 Stunde</button>
                <button type="button" onClick={() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); setForm({ ...form, reminder_at: d.toISOString() }); }} className="rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600">Morgen 09:00</button>
                <button type="button" onClick={() => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); setForm({ ...form, reminder_at: d.toISOString() }); }} className="rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600">Nächste Woche</button>
              </div>
            </div>
            <div>
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <Label>{form.kind === "checklist" ? "Zusätzliche Notiz (optional)" : "Notiz *"}</Label>
                {speechSupported && (
                  <div className="flex items-center gap-1.5">
                    <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs">
                      <button
                        type="button"
                        onClick={() => { if (listening) stopDictation(); setSpeechLang("de-DE"); }}
                        className={`rounded-md px-2 py-1 ${speechLang === "de-DE" ? "bg-white font-semibold text-indigo-700 shadow-sm" : "text-slate-500"}`}
                      >
                        DE
                      </button>
                      <button
                        type="button"
                        onClick={() => { if (listening) stopDictation(); setSpeechLang("ro-RO"); }}
                        className={`rounded-md px-2 py-1 ${speechLang === "ro-RO" ? "bg-white font-semibold text-indigo-700 shadow-sm" : "text-slate-500"}`}
                      >
                        RO
                      </button>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant={listening ? "destructive" : "outline"}
                      onClick={() => startDictation()}
                      className="min-w-28"
                      data-testid="note-dictation"
                    >
                      {listening ? <MicOff className="mr-1.5 h-4 w-4" /> : <Mic className="mr-1.5 h-4 w-4" />}
                      {listening ? "Stoppen" : "Diktieren"}
                    </Button>
                  </div>
                )}
              </div>
              <Textarea
                rows={form.kind === "checklist" ? 5 : 12}
                value={form.content}
                onChange={(event) => setForm({ ...form, content: event.target.value })}
                placeholder="Text eingeben…"
                className="resize-y"
                data-testid="note-content"
              />
            </div>
          </div>
          <DialogFooter className="shrink-0 gap-2 border-t bg-background px-5 py-4 sm:px-6">
            <Button variant="outline" onClick={() => handleDialogChange(false)}>Abbrechen</Button>
            <Button
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending || (!form.content.trim() && !form.checklist.some((item) => item.text.trim()))}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-note"
            >
              {saveMut.isPending ? "Speichern…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
